/**
 * Sentry utilities for proper error handling
 */

import * as Sentry from '@sentry/react'
import type { Event } from '@sentry/react'

/**
 * iOS WebKit (Safari, Chrome-on-iOS, Google Search App) sometimes exhausts
 * its call stack and surfaces `RangeError: Maximum call stack size exceeded.`
 * through `window.onerror`. The stack is truncated and cannot be resolved to a
 * source file, so Sentry only sees frames pointing at the current page URL.
 * This pattern is browser/engine noise, not an actionable app regression.
 */
const STACK_OVERFLOW_NOISE_PATTERN = /^Maximum call stack size exceeded\.?$/

/**
 * Type definition for Supabase error objects
 */
interface SupabaseError {
  message?: string
  code?: string
  details?: string
  hint?: string
  [key: string]: unknown
}

/**
 * Converts various error types to a proper Error object for Sentry
 * This prevents Sentry from treating Supabase error objects as plain objects
 * which results in misleading error messages like "Object captured as exception"
 *
 * @param error - The error to normalize (can be Error, object, or string)
 * @returns A proper Error object with meaningful information
 */
export function normalizeError(error: unknown): Error {
  // If it's already an Error instance, return it
  if (error instanceof Error) {
    return error
  }

  // If it's a Supabase-like error object
  if (error && typeof error === 'object' && !Array.isArray(error)) {
    const supabaseError = error as SupabaseError

    // Use the message if available, otherwise construct one
    const message = supabaseError.message || 'Unknown error occurred'
    const normalizedError = new Error(message)

    // Set error name from code if available
    if (supabaseError.code) {
      normalizedError.name = `SupabaseError: ${supabaseError.code}`
    }

    // Attach additional properties as non-enumerable to the error
    if (supabaseError.code) {
      Object.defineProperty(normalizedError, 'code', { value: supabaseError.code, enumerable: false })
    }
    if (supabaseError.details) {
      Object.defineProperty(normalizedError, 'details', { value: supabaseError.details, enumerable: false })
    }
    if (supabaseError.hint) {
      Object.defineProperty(normalizedError, 'hint', { value: supabaseError.hint, enumerable: false })
    }

    // Copy any other properties from the original error
    Object.keys(supabaseError).forEach(key => {
      if (key !== 'message' && key !== 'code' && key !== 'details' && key !== 'hint') {
        Object.defineProperty(normalizedError, key, { value: supabaseError[key], enumerable: false })
      }
    })

    return normalizedError
  }

  // If it's a string, convert it to an Error
  if (typeof error === 'string') {
    return new Error(error)
  }

  // Fallback: create a generic error with the value as message
  return new Error(String(error))
}

/**
 * Capture an exception in Sentry, automatically normalizing Supabase errors
 *
 * @param error - The error to capture
 * @param captureContext - Additional context to include (optional)
 */
export function captureException(error: unknown, captureContext?: Parameters<typeof Sentry.captureException>[1]): void {
  const normalizedError = normalizeError(error)
  Sentry.captureException(normalizedError, captureContext)
}

/**
 * Drop iOS WebKit stack-overflow noise while preserving real recursions.
 *
 * A genuine infinite recursion in Classella source will carry at least one
 * frame that resolves to a JS/TS chunk or source file (e.g. /assets/...js or
 * src/...tsx). The noise event has no resolvable source — every frame is
 * either missing or points at the current page URL.
 *
 * @param event - The Sentry event being sent
 * @returns true if the event should be discarded
 */
export function shouldIgnoreSentryEvent(event: Event): boolean {
  const primary = event.exception?.values?.find(Boolean)
  if (!primary || primary.type !== 'RangeError') {
    return false
  }

  const rawMessage = normalizeString(primary.value ?? event.message)
  const message = stripErrorWrappers(rawMessage)
  if (!STACK_OVERFLOW_NOISE_PATTERN.test(message)) {
    return false
  }

  const osName = normalizeString(event.contexts?.os?.name)
  if (osName !== 'iOS') {
    return false
  }

  const mechanism = normalizeString(primary.mechanism?.type)
  if (mechanism !== 'auto.browser.global_handlers.onerror' && mechanism !== 'onerror') {
    return false
  }

  const frames = primary.stacktrace?.frames ?? []
  if (frames.length === 0) {
    return true
  }

  for (const frame of frames) {
    const filename = normalizeString(frame.filename ?? frame.abs_path)
    if (filename && filename !== 'undefined' && isRealScriptSource(filename)) {
      return false
    }
  }

  return true
}

function normalizeString(value: unknown): string {
  if (typeof value === 'string') {
    return value
  }
  return value == null ? '' : String(value)
}

function stripErrorWrappers(message: string): string {
  return message
    .replace(/^RangeError:\s*/, '')
    .replace(/^Unhandled promise rejection:\s*/, '')
    .trim()
}

function isRealScriptSource(filename: string): boolean {
  if (!filename || filename === 'undefined') {
    return false
  }

  const lower = filename.toLowerCase()
  return (
    lower.endsWith('.js') ||
    lower.endsWith('.mjs') ||
    lower.endsWith('.cjs') ||
    lower.endsWith('.ts') ||
    lower.endsWith('.tsx') ||
    lower.endsWith('.jsx') ||
    filename.includes('/assets/') ||
    filename.includes('/src/') ||
    filename.includes('node_modules') ||
    filename.includes('webpack://') ||
    filename.startsWith('app://')
  )
}
